import React from 'react';
import { MapPin, ChevronUp, ChevronDown, Filter, List, BarChart3 } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';
import RegionSelector from './RegionSelector';
import mapboxgl from 'mapbox-gl';
import { StationInfo } from '../../services/stationService';

interface StationControlPanelProps {
  isExpanded: boolean;
  onToggle: () => void;
  map: mapboxgl.Map | null;
  selectedRegion: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  onRegionSelect: (
    bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    } | null,
  ) => void;
  filteredStations: StationInfo[];
  totalStations: number;
  onShowStationsList: () => void;
  onShowBenchmarks: () => void;
  onDrawingStateChange?: (isDrawing: boolean) => void;
  isMobile?: boolean;
}

const StationControlPanel: React.FC<StationControlPanelProps> = ({
  isExpanded,
  onToggle,
  map,
  selectedRegion,
  onRegionSelect,
  filteredStations,
  totalStations,
  onShowStationsList,
  onShowBenchmarks,
  onDrawingStateChange,
  isMobile,
}) => {
  const { theme } = useTheme();

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
      className="relative group"
    >
      {/* Main Control Panel */}
      <div
        style={{
          backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          width: '240px',
          maxWidth: '90vw',
          maxHeight: isMobile ? 'calc(40vh - 20px)' : 'calc(60vh - 20px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? '8px' : '8px 12px',
            borderBottom: isExpanded
              ? `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`
              : 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
              cursor: 'pointer',
            }}
            onClick={onToggle}
          >
            <Filter size={14} style={{ marginRight: '6px' }} />
            <span style={{ fontWeight: 500, fontSize: '13px', flex: 1 }}>Station Filters</span>
            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </div>
        </div>

        {/* Content */}
        {isExpanded && (
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              padding: isMobile ? '8px' : '8px 12px',
              gap: '12px',
            }}
            className="custom-scrollbar"
          >
            {/* Region Selection */}
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '6px',
                  color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                  fontSize: '11px',
                  fontWeight: '500',
                }}
              >
                <MapPin size={11} style={{ marginRight: '4px' }} />
                Region Selection
              </div>
              <RegionSelector
                map={map}
                selectedRegion={selectedRegion}
                onRegionSelect={onRegionSelect}
                onDrawingStateChange={onDrawingStateChange}
              />
            </div>

            {/* Statistics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '12px', color: theme === 'dark' ? '#9CA3AF' : '#6B7280' }}>
                Filtered:{' '}
                <span
                  style={{ fontWeight: '600', color: theme === 'dark' ? '#F9FAFB' : '#1F2937' }}
                >
                  {filteredStations.length}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: theme === 'dark' ? '#9CA3AF' : '#6B7280' }}>
                Total:{' '}
                <span
                  style={{ fontWeight: '600', color: theme === 'dark' ? '#F9FAFB' : '#1F2937' }}
                >
                  {totalStations}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={onShowStationsList}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 12px',
                  backgroundColor: '#E34290',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#c73678';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#E34290';
                }}
              >
                <List size={14} />
                View Stations ({filteredStations.length})
              </button>

              <div className="relative group">
                <button
                  onClick={onShowBenchmarks}
                  disabled={!selectedRegion || filteredStations.length === 0}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 12px',
                    backgroundColor:
                      selectedRegion && filteredStations.length > 0
                        ? 'transparent'
                        : theme === 'dark'
                          ? '#374151'
                          : '#E5E7EB',
                    color:
                      selectedRegion && filteredStations.length > 0
                        ? '#E34290'
                        : theme === 'dark'
                          ? '#9CA3AF'
                          : '#9CA3AF',
                    border:
                      selectedRegion && filteredStations.length > 0 ? '2px solid #E34290' : 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor:
                      selectedRegion && filteredStations.length > 0 ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    opacity: selectedRegion && filteredStations.length > 0 ? 1 : 0.6,
                  }}
                  onMouseOver={(e) => {
                    if (selectedRegion && filteredStations.length > 0) {
                      e.currentTarget.style.backgroundColor = 'rgba(227, 66, 144, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedRegion && filteredStations.length > 0) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <BarChart3 size={14} />
                  Compute Benchmarks
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tooltip for disabled state - positioned below the panel */}
      {(!selectedRegion || filteredStations.length === 0) && (
        <div
          className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-[9999]"
          style={{
            left: '0px',
            top: '100%',
            marginTop: '8px',
          }}
        >
          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
            <div className="flex items-center gap-2">
              <span className="text-blue-400">📍</span>
              {!selectedRegion
                ? 'Select region first to compute benchmarks'
                : 'No stations in selected region'}
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationControlPanel;
